import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";

const customModalStyles = {
  content: {
    padding: "20px",
    width: "80%",
    maxWidth: "600px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
};

const fieldStyle = {
  width: "100%",
  padding: "8px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #ccc",
};

const labelStyle = {
  display: "block",
  fontWeight: "bold",
  marginBottom: "5px",
};

const emptyForm = {
  name: "",
  slug: "",
  description: "",
  order: "",
  isActive: true,
};

const SdcBatches = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("sdc/batches/all");
      setItems(res.data?.data || []);
    } catch (error) {
      Swal.fire("Error", "Failed to load batches", "error");
    }
    setLoading(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingId(item._id);
    setForm({
      name: item.name || "",
      slug: item.slug || "",
      description: item.description || "",
      order: item.order ?? "",
      isActive: item.isActive !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      Swal.fire("Missing name", "Batch name is required", "warning");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        description: form.description,
        isActive: form.isActive,
      };
      if (form.order !== "" && form.order !== null) {
        payload.order = Number(form.order);
      }

      if (editingId) {
        await axiosInstance.put(`sdc/batches/change/${editingId}`, payload);
        Swal.fire("Saved", "Batch updated", "success");
      } else {
        await axiosInstance.post("sdc/batches/create", payload);
        Swal.fire("Saved", "Batch created", "success");
      }
      setShowModal(false);
      fetchItems();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save batch",
        "error"
      );
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this batch?",
      text: "Programs under it will also be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`sdc/batches/delete/${id}`);
      fetchItems();
      Swal.fire("Deleted", "Batch removed", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete batch",
        "error"
      );
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>SDC Batches</h2>
          <p style={{ margin: "6px 0 0", color: "#666" }}>
            Batches appear as tabs on NAVTTC Courses/Trainings.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          style={{
            padding: "10px 20px",
            backgroundColor: "#4CAF50",
            color: "#fff",
            border: "none",
            borderRadius: 5,
            cursor: "pointer",
          }}
        >
          Add Batch
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <CircularProgress />
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
          {items.length ? (
            items.map((item) => (
              <div
                key={item._id}
                style={{
                  width: 280,
                  padding: 16,
                  borderRadius: 8,
                  background: "#fff",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              >
                <h3 style={{ marginTop: 0 }}>{item.name}</h3>
                <p style={{ fontSize: 13, color: "#666", margin: "4px 0" }}>
                  Slug: {item.slug}
                </p>
                <p style={{ fontSize: 13, color: "#666", margin: "4px 0" }}>
                  Programs: {item.programsCount ?? 0} · Order: {item.order ?? 0}
                </p>
                <p style={{ fontSize: 13, margin: "4px 0" }}>
                  {item.isActive === false ? "Inactive" : "Active"}
                </p>
                <Actions
                  courseId={item._id}
                  onEdit={() => openEdit(item)}
                  onDelete={handleDelete}
                />
              </div>
            ))
          ) : (
            <p>No batches yet. Add PMYSDP batches to get started.</p>
          )}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        style={customModalStyles}
        contentLabel="Batch"
      >
        <h2>{editingId ? "Edit Batch" : "Add Batch"}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Name</label>
            <input
              style={fieldStyle}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Slug (optional)</label>
            <input
              style={fieldStyle}
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="auto from name"
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Description</label>
            <textarea
              style={{ ...fieldStyle, minHeight: 80 }}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Order</label>
            <input
              type="number"
              style={fieldStyle}
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.checked })
                }
              />
              Active
            </label>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4CAF50",
                color: "#fff",
                border: "none",
                borderRadius: 5,
                cursor: "pointer",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#ccc",
                border: "none",
                borderRadius: 5,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SdcBatches;
